// pages/strategic-playbook/tools/asymmetry.js
// ════════════════════════════════════════════════════
// ASYMMETRY CALCULATOR
// ════════════════════════════════════════════════════
// Is this bet worth taking?
// Forces probability-weighted thinking instead of vibes.
//
// Connects to: Thiel (rare contrarian truths)
// ════════════════════════════════════════════════════

import { useState, useMemo } from 'react';
import Head from 'next/head';
import Link from 'next/link';

export default function AsymmetryCalculator() {
  const [winProb, setWinProb] = useState(40);
  const [upsidePct, setUpsidePct] = useState(100);
  const [downsidePct, setDownsidePct] = useState(20);
  const [timeHorizonYears, setTimeHorizonYears] = useState(2);

  const result = useMemo(() => {
    return analyze({
      winProb: winProb / 100,
      upsidePct: upsidePct / 100,
      downsidePct: downsidePct / 100,
      timeHorizonYears,
    });
  }, [winProb, upsidePct, downsidePct, timeHorizonYears]);

  return (
    <div className="sp-root">
      <Head>
        <title>Asymmetry Calculator · Strategic Intelligence</title>
      </Head>

      <div className="sp-page">
        <ToolTopbar />

        <div style={{ marginBottom: 32 }}>
          <Link
            href="/strategic-playbook/tools"
            className="sp-reader-back"
            style={{ marginBottom: 16 }}
          >
            ← Tools
          </Link>
          <div
            style={{
              fontFamily: 'var(--sp-font-mono)',
              fontSize: 10,
              textTransform: 'uppercase',
              letterSpacing: '0.20em',
              color: 'var(--sp-aged-brass)',
              marginBottom: 14,
            }}
          >
            Decision Sizing · Calculator
          </div>
          <h1
            style={{
              fontFamily: 'var(--sp-font-serif)',
              fontWeight: 500,
              fontSize: 32,
              lineHeight: 1.2,
              color: 'var(--sp-warm-cream)',
              margin: '0 0 14px 0',
              letterSpacing: '-0.01em',
            }}
          >
            Asymmetry Calculator
          </h1>
          <p
            style={{
              fontSize: 14,
              lineHeight: 1.65,
              color: 'rgba(232, 221, 196, 0.78)',
              maxWidth: 700,
              margin: 0,
            }}
          >
            Is this bet worth taking? Calculates expected value, risk/reward ratio, and
            verdict. Connects to Thiel&apos;s framework — most ideas have symmetric or
            negative payoffs; the asymmetric ones are rare and worth concentrated bets.
          </p>
        </div>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: 24,
          }}
          className="asymmetry-grid"
        >
          {/* INPUTS */}
          <div
            style={{
              padding: '28px',
              background: 'var(--sp-soft-charcoal)',
              border: '1px solid var(--sp-divider)',
              borderRadius: 'var(--sp-radius-card)',
            }}
          >
            <h3
              style={{
                fontFamily: 'var(--sp-font-mono)',
                fontSize: 10,
                textTransform: 'uppercase',
                letterSpacing: '0.18em',
                color: 'var(--sp-aged-brass)',
                margin: '0 0 22px 0',
              }}
            >
              Inputs
            </h3>

            <Field
              label="Probability of being right"
              suffix="%"
              hint="The hardest input. Be honest. Most overestimate."
            >
              <NumberInput value={winProb} onChange={setWinProb} step={1} min={0} max={100} />
            </Field>

            <Field
              label="Upside if right"
              suffix="% gain"
              hint="What you make if the thesis works (vs current price/state)."
            >
              <NumberInput value={upsidePct} onChange={setUpsidePct} step={5} min={0} />
            </Field>

            <Field
              label="Downside if wrong"
              suffix="% loss"
              hint="Maximum loss tolerance (where would you exit?)."
            >
              <NumberInput value={downsidePct} onChange={setDownsidePct} step={1} min={0} />
            </Field>

            <Field
              label="Time horizon"
              suffix="years"
              hint="How long until thesis confirms or breaks."
            >
              <NumberInput
                value={timeHorizonYears}
                onChange={setTimeHorizonYears}
                step={0.5}
                min={0.1}
              />
            </Field>

            {/* Quick presets */}
            <div style={{ marginTop: 20 }}>
              <div
                style={{
                  fontFamily: 'var(--sp-font-mono)',
                  fontSize: 10,
                  textTransform: 'uppercase',
                  letterSpacing: '0.14em',
                  color: 'rgba(232, 221, 196, 0.45)',
                  marginBottom: 8,
                }}
              >
                Quick presets
              </div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <PresetButton
                  label="VC bet"
                  onClick={() => {
                    setWinProb(15);
                    setUpsidePct(2000);
                    setDownsidePct(100);
                    setTimeHorizonYears(7);
                  }}
                />
                <PresetButton
                  label="Concentrated value"
                  onClick={() => {
                    setWinProb(70);
                    setUpsidePct(50);
                    setDownsidePct(20);
                    setTimeHorizonYears(3);
                  }}
                />
                <PresetButton
                  label="Index hold"
                  onClick={() => {
                    setWinProb(85);
                    setUpsidePct(8);
                    setDownsidePct(15);
                    setTimeHorizonYears(10);
                  }}
                />
                <PresetButton
                  label="Coin flip"
                  onClick={() => {
                    setWinProb(50);
                    setUpsidePct(20);
                    setDownsidePct(20);
                    setTimeHorizonYears(1);
                  }}
                />
              </div>
            </div>
          </div>

          {/* OUTPUTS */}
          <div>
            {/* Verdict — large card */}
            <div
              style={{
                padding: '28px',
                background:
                  'linear-gradient(135deg, var(--sp-card-charcoal) 0%, var(--sp-soft-charcoal) 100%)',
                border: `1px solid ${result.verdictColor}`,
                borderRadius: 'var(--sp-radius-card)',
                marginBottom: 16,
              }}
            >
              <div
                style={{
                  fontFamily: 'var(--sp-font-mono)',
                  fontSize: 10,
                  textTransform: 'uppercase',
                  letterSpacing: '0.18em',
                  color: result.verdictColor,
                  marginBottom: 14,
                }}
              >
                Verdict
              </div>
              <div
                style={{
                  fontFamily: 'var(--sp-font-serif)',
                  fontSize: 32,
                  lineHeight: 1.15,
                  color: 'var(--sp-warm-cream)',
                  marginBottom: 12,
                  letterSpacing: '-0.01em',
                }}
              >
                {result.verdictHeadline}
              </div>
              <div
                style={{
                  fontFamily: 'var(--sp-font-mono)',
                  fontSize: 13,
                  color: result.verdictColor,
                  marginBottom: 18,
                  letterSpacing: '0.05em',
                }}
              >
                Reward / risk: <strong>{result.rrRatio}</strong>
              </div>
              <div
                style={{
                  fontFamily: 'var(--sp-font-serif)',
                  fontStyle: 'italic',
                  fontSize: 14,
                  lineHeight: 1.6,
                  color: 'var(--sp-warm-cream)',
                  borderLeft: `2px solid ${result.verdictColor}`,
                  paddingLeft: 14,
                }}
              >
                {result.verdictBody}
              </div>
            </div>

            {/* Math breakdown */}
            <div
              style={{
                padding: '24px 28px',
                background: 'var(--sp-soft-charcoal)',
                border: '1px solid var(--sp-divider)',
                borderRadius: 'var(--sp-radius-card)',
                marginBottom: 16,
              }}
            >
              <h3
                style={{
                  fontFamily: 'var(--sp-font-mono)',
                  fontSize: 10,
                  textTransform: 'uppercase',
                  letterSpacing: '0.18em',
                  color: 'var(--sp-aged-brass)',
                  margin: '0 0 18px 0',
                }}
              >
                The Math
              </h3>
              <ResultRow
                label="Expected value (per dollar bet)"
                value={result.evDisplay}
                color={result.evColor}
              />
              <ResultRow label="Expected value annualized" value={result.evAnnualizedDisplay} />
              <ResultRow label="Reward/risk ratio" value={result.rrRatio} />
              <ResultRow
                label="Break-even win probability"
                value={result.breakEvenPct}
                hint="You need to be right at least this often"
              />
              <ResultRow
                label="Asymmetry score"
                value={result.asymmetryLabel}
                color={result.verdictColor}
                highlight
              />
            </div>

            {/* Honest read */}
            <div
              style={{
                padding: '20px 24px',
                background: 'var(--sp-parchment-bg)',
                border: '1px solid var(--sp-divider)',
                borderRadius: 'var(--sp-radius-card)',
              }}
            >
              <div
                style={{
                  fontFamily: 'var(--sp-font-mono)',
                  fontSize: 10,
                  textTransform: 'uppercase',
                  letterSpacing: '0.16em',
                  color: 'var(--sp-aged-brass)',
                  marginBottom: 10,
                }}
              >
                Honest Read
              </div>
              <div style={{ fontSize: 13, lineHeight: 1.6, color: 'rgba(232, 221, 196, 0.78)' }}>
                {result.honestRead}
              </div>
            </div>

            <div style={{ marginTop: 16, fontSize: 12, color: 'rgba(232, 221, 196, 0.55)' }}>
              <span
                style={{
                  fontFamily: 'var(--sp-font-mono)',
                  fontSize: 10,
                  letterSpacing: '0.14em',
                  textTransform: 'uppercase',
                  color: 'rgba(232, 221, 196, 0.45)',
                  marginRight: 8,
                }}
              >
                Connected dossiers:
              </span>
              <Link
                href="/strategic-playbook/thiel-contrarian-truths"
                style={{ color: 'var(--sp-aged-brass)' }}
              >
                Thiel
              </Link>
              {' · '}
              <Link
                href="/strategic-playbook/taleb-antifragile"
                style={{ color: 'var(--sp-aged-brass)' }}
              >
                Taleb
              </Link>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        @media (max-width: 900px) {
          .asymmetry-grid {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </div>
  );
}

// ─── Calculation logic ─────────────────────────────
function analyze({ winProb, upsidePct, downsidePct, timeHorizonYears }) {
  const lossProb = 1 - winProb;

  // Expected value per dollar bet
  const evPerDollar = winProb * upsidePct - lossProb * downsidePct;
  const evPct = evPerDollar * 100;

  // Reward/risk ratio
  const rrRatio =
    downsidePct > 0 ? (upsidePct / downsidePct).toFixed(2) + ':1' : '∞:1';
  const rrNumeric = downsidePct > 0 ? upsidePct / downsidePct : Infinity;

  // Break-even win probability — point at which EV = 0
  const breakEven =
    upsidePct + downsidePct > 0
      ? (downsidePct / (upsidePct + downsidePct)) * 100
      : 0;

  // Annualize (CAGR-style approximation for held bets)
  const totalReturnIfWon = upsidePct * winProb - downsidePct * lossProb;
  const annualizedReturn =
    timeHorizonYears > 0
      ? Math.pow(1 + totalReturnIfWon, 1 / timeHorizonYears) - 1
      : totalReturnIfWon;
  const evAnnualizedPct = annualizedReturn * 100;

  // Verdict logic
  let verdictHeadline, verdictBody, verdictColor, asymmetryLabel, honestRead;

  if (evPerDollar < 0) {
    verdictHeadline = 'Don\'t take this bet.';
    verdictBody = `Negative expected value (${evPct >= 0 ? '+' : ''}${evPct.toFixed(2)}¢ per dollar). At these inputs, you lose money over time.`;
    verdictColor = 'var(--sp-oxblood)';
    asymmetryLabel = 'Negative EV — pass';
    honestRead = `You need to be right ${breakEven.toFixed(0)}% of the time to break even. Either your win probability is too low, your upside too small, or your downside too large. Re-examine each input — overestimating probability is the most common error.`;
  } else if (rrNumeric >= 5 && winProb >= 0.20) {
    verdictHeadline = 'Asymmetric — Thiel-style.';
    verdictBody = `${rrRatio} reward/risk with ${(winProb * 100).toFixed(0)}% probability. Few bets clear this bar. When they do, concentrate.`;
    verdictColor = 'var(--sp-aged-brass)';
    asymmetryLabel = 'Strongly asymmetric';
    honestRead = `This is the kind of bet Thiel sizes for impact. Win rate doesn't need to be high — the 5:1+ reward/risk does the work. Verify your inputs aren't optimistic. If still asymmetric, position-size accordingly via Position Sizer tool.`;
  } else if (rrNumeric >= 3 && evPct > 0) {
    verdictHeadline = 'Decent asymmetry.';
    verdictBody = `${rrRatio} reward/risk with positive EV (+${evPct.toFixed(2)}¢ per dollar). Worth taking with disciplined sizing.`;
    verdictColor = 'var(--sp-forest-moss)';
    asymmetryLabel = 'Asymmetric';
    honestRead = `Good profile. Half-Kelly sizing is the right discipline (use Position Sizer tool). Don't size full Kelly — your probability estimate is uncertain.`;
  } else if (rrNumeric < 1.5) {
    verdictHeadline = 'Symmetric — pass or scale down.';
    verdictBody = `${rrRatio} is too tight — you need ${breakEven.toFixed(0)}%+ win rate to break even, and you're only at ${(winProb * 100).toFixed(0)}%.`;
    verdictColor = 'var(--sp-cognac)';
    asymmetryLabel = 'Symmetric';
    honestRead = `Tight reward/risk requires high win probability. Either find a better setup or skip. Most "interesting" trades cluster here — that's why most active traders break even or lose to costs.`;
  } else {
    verdictHeadline = 'Marginal.';
    verdictBody = `${rrRatio} reward/risk, EV ${evPct >= 0 ? '+' : ''}${evPct.toFixed(2)}¢. Edge is real but small.`;
    verdictColor = 'var(--sp-cognac)';
    asymmetryLabel = 'Marginal';
    honestRead = `Profile is okay but not exciting. Take only if you have additional conviction beyond these numbers (e.g., information edge, contrarian thesis). Otherwise pass — opportunity cost matters.`;
  }

  return {
    evDisplay:
      (evPct >= 0 ? '+' : '') + evPct.toFixed(2) + '¢',
    evColor: evPct >= 0 ? 'var(--sp-forest-moss)' : 'var(--sp-oxblood)',
    evAnnualizedDisplay:
      (evAnnualizedPct >= 0 ? '+' : '') + evAnnualizedPct.toFixed(2) + '%',
    rrRatio,
    breakEvenPct: breakEven.toFixed(1) + '%',
    asymmetryLabel,
    verdictHeadline,
    verdictBody,
    verdictColor,
    honestRead,
  };
}

// ─── UI primitives ─────────────────────────────────
function Field({ label, suffix, hint, children }) {
  return (
    <div style={{ marginBottom: 18 }}>
      <div
        style={{
          fontFamily: 'var(--sp-font-mono)',
          fontSize: 10,
          textTransform: 'uppercase',
          letterSpacing: '0.14em',
          color: 'rgba(232, 221, 196, 0.55)',
          marginBottom: 6,
          display: 'flex',
          justifyContent: 'space-between',
        }}
      >
        <span>{label}</span>
        {suffix && <span style={{ color: 'var(--sp-aged-brass)' }}>{suffix}</span>}
      </div>
      {children}
      {hint && (
        <div
          style={{
            fontSize: 11,
            color: 'rgba(232, 221, 196, 0.50)',
            marginTop: 4,
            lineHeight: 1.5,
          }}
        >
          {hint}
        </div>
      )}
    </div>
  );
}

const inputStyle = {
  width: '100%',
  padding: '10px 14px',
  background: 'var(--sp-charcoal-black)',
  border: '1px solid var(--sp-divider)',
  borderRadius: 'var(--sp-radius)',
  color: 'var(--sp-warm-cream)',
  fontFamily: 'var(--sp-font-body)',
  fontSize: 14,
  outline: 'none',
};

function NumberInput({ value, onChange, step, min, max }) {
  return (
    <input
      type="number"
      value={value}
      step={step}
      min={min}
      max={max}
      onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
      style={inputStyle}
    />
  );
}

function PresetButton({ label, onClick }) {
  return (
    <button
      onClick={onClick}
      style={{
        fontFamily: 'var(--sp-font-mono)',
        fontSize: 10,
        padding: '6px 12px',
        background: 'var(--sp-charcoal-black)',
        border: '1px solid var(--sp-divider)',
        borderRadius: 'var(--sp-radius)',
        color: 'rgba(232, 221, 196, 0.65)',
        cursor: 'pointer',
        textTransform: 'uppercase',
        letterSpacing: '0.10em',
      }}
    >
      {label}
    </button>
  );
}

function ResultRow({ label, value, color, highlight, hint }) {
  return (
    <div
      style={{
        padding: '8px 0',
        borderBottom: '1px solid rgba(58, 47, 40, 0.5)',
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <span
          style={{
            fontSize: 12,
            color: highlight ? 'var(--sp-warm-cream)' : 'rgba(232, 221, 196, 0.65)',
          }}
        >
          {label}
        </span>
        <span
          style={{
            fontFamily: 'var(--sp-font-mono)',
            fontSize: 13,
            color: color || (highlight ? 'var(--sp-aged-brass)' : 'var(--sp-warm-cream)'),
            fontWeight: highlight ? 600 : 400,
          }}
        >
          {value}
        </span>
      </div>
      {hint && (
        <div
          style={{
            fontSize: 10,
            color: 'rgba(232, 221, 196, 0.45)',
            marginTop: 2,
          }}
        >
          {hint}
        </div>
      )}
    </div>
  );
}

function ToolTopbar() {
  return (
    <div className="sp-topbar">
      <div>
        <h1>Strategic Intelligence</h1>
        <div className="sp-topbar-meta">Tools · Asymmetry Calculator</div>
      </div>
      <nav className="sp-topbar-nav">
        <Link href="/strategic-playbook">Dashboard</Link>
        <Link href="/strategic-playbook/library">Library</Link>
        <Link href="/strategic-playbook/tools" className="sp-active">
          Tools
        </Link>
        <Link href="/strategic-playbook/decisions">Decisions</Link>
        <Link href="/strategic-playbook/simulate">Simulate</Link>
      </nav>
    </div>
  );
}
